import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, IconButton, Popover, Box, Typography, Button, List, ListItemButton,
  ListItemText, Avatar, Divider, Tooltip,
} from '@mui/material';
import {
  Notifications as BellIcon,
  AssignmentTurnedIn as ReviewIcon,
  Science as NpaIcon,
  Shield as RiskIcon,
} from '@mui/icons-material';
import * as inboxApi from '../services/inboxApi';
import { INBOX_MODULES } from '../constants/inbox';

const POLL_MS = 60000;

function moduleIcon(module) {
  switch (module) {
    case INBOX_MODULES.NPA: return <NpaIcon fontSize="small" />;
    case INBOX_MODULES.RISK: return <RiskIcon fontSize="small" />;
    default: return <ReviewIcon fontSize="small" />;
  }
}

function relativeTime(value) {
  if (!value) return '';
  const mins = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const timer = useRef(null);

  const refreshCount = useCallback(() => {
    inboxApi.getUnreadCount().then(setCount).catch(() => {});
  }, []);

  useEffect(() => {
    refreshCount();
    timer.current = setInterval(refreshCount, POLL_MS);
    // The Inbox To-Do marks notifications read when an item is opened — refresh promptly so the
    // badge stays in sync without waiting for the next poll.
    window.addEventListener('inbox-unread-changed', refreshCount);
    return () => {
      clearInterval(timer.current);
      window.removeEventListener('inbox-unread-changed', refreshCount);
    };
  }, [refreshCount]);

  const open = (e) => {
    setAnchor(e.currentTarget);
    inboxApi.getNotifications({ take: 10 })
      .then((feed) => {
        setItems(feed.items ?? []);
        setCount(feed.unreadCount ?? 0);
      })
      .catch(() => setItems([]));
  };

  const close = () => setAnchor(null);

  const openItem = async (n) => {
    close();
    if (!n.isRead) {
      await inboxApi.markRead(n.id);
      setCount((c) => Math.max(0, c - 1));
    }
    if (n.actionUrl) navigate(n.actionUrl);
  };

  const markAll = async () => {
    await inboxApi.markAllRead();
    setItems((prev) => prev.map((i) => ({ ...i, isRead: true })));
    setCount(0);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={open} sx={{ color: 'text.secondary' }}>
          <Badge badgeContent={count} color="error" max={99}>
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '90vw' } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
          <Button size="small" onClick={markAll} disabled={count === 0}>Mark all read</Button>
        </Box>
        <Divider />

        {items.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">You're all caught up.</Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {items.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => openItem(n)}
                sx={{ alignItems: 'flex-start', gap: 1.25, bgcolor: n.isRead ? 'transparent' : 'action.hover' }}
              >
                <Avatar variant="rounded" sx={{ width: 34, height: 34, bgcolor: 'action.selected', color: 'text.secondary' }}>
                  {moduleIcon(n.module)}
                </Avatar>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={n.isRead ? 500 : 700}>{n.title}</Typography>}
                  secondary={
                    <>
                      {n.message && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                          {n.message}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">{relativeTime(n.createdAt)}</Typography>
                    </>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Button size="small" fullWidth onClick={() => { close(); navigate('/inbox'); }}>
            View all in Inbox
          </Button>
        </Box>
      </Popover>
    </>
  );
}
